import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import NewStock from '../components/Inventory/NewStock';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
  Card,
  CardContent,
  Container,
  Fade,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import UsedStock from '../components/Inventory/UsedStock';
import FaultyStock from '../components/Inventory/FaultyStock';
import SavedStock from '../components/Inventory/SavedStock';
import BundledStock from '../components/Inventory/BundledStock';
import { DownloadRounded, Assessment, Inventory2, TrendingUp } from '@mui/icons-material';

// services
import URLS from '../services/urlConstants';
import { get } from '../services/apiMethods';
import { downloadExcel } from '../utils/utilities';
import DashBoardPageHeaderPage from '../components/DashBoard/DashBoardHeaderPage';
import WarehouseContext from '../context/WarehouseContext';
import ThemeModeContext from '../context/ThemeModeContext';
import ShippedStock from '../components/Inventory/ShippedStock';
import { PuffLoader } from 'react-spinners';
import ShowComponent from '../components/ShowComponent';
import { FileDownload } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';

export default function Inventory() {
  const { selectedWarehouse, selectedOwnerCode, selectedOwnerGroup } = useContext(WarehouseContext);
  const { isDarkMode } = useContext(ThemeModeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [newStock, setNewStock] = useState([]);
  const [usedStock, setUsedStock] = useState([]);
  const [faultyStock, setFaultyStock] = useState([]);
  const [savedStock, setSavedStock] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [subFamily, setSubFamily] = useState([]);
  const [shippedStock, setShippedStock] = useState([]);
  const [bundleTypes, setBundleTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingTotalStock, setDownloadingTotalStock] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const isLoadingRef = useRef(false);

  const handleDownloadReportOrderWise = useCallback(async () => {
    try {
      // Block action if data is still loading
      if (loading || isLoadingRef.current) {
        return toast.info('Please wait for data to load before downloading!');
      }

      if (!selectedWarehouse.length) return toast.info('Please select a warehouse!');
      setDownloading(true);
      const { data } = await get(URLS.items.downloadTotalStockOrderWise, {
        params: {
          warehouses: selectedWarehouse,
          ownerCodes: selectedOwnerCode,
          ownerGroups: selectedOwnerGroup,
        },
        responseType: 'blob',
      });
      downloadExcel(data, 'Total Stock');
      setDownloading(false);
    } catch (error) {
      console.log(error);
      toast.error('Failed to download report');
      setDownloading(false);
    }
  }, [selectedWarehouse, selectedOwnerCode, selectedOwnerGroup]);

  const getAllBundleTypes = useCallback(async () => {
    try {
      const { data } = await get(URLS.bundleType.list);
      setBundleTypes(data.result);
    } catch (error) {
      console.log(error);
      toast.error('Failed to load bundle types');
    }
  }, []);

  const getSubFamily = useCallback(async () => {
    try {
      const { data } = await get(URLS.subFamily.list);
      setSubFamily(data.result);
    } catch (error) {
      console.log(error);
      toast.error('Failed to load sub families');
    }
  }, []);

  useEffect(() => {
    getAllBundleTypes();
  }, [getAllBundleTypes]);

  const getAllData = useCallback(async () => {
    try {
      // Prevent multiple simultaneous requests
      if (isLoadingRef.current) return;

      // Don't fetch data if no warehouses are selected
      if (!selectedWarehouse || selectedWarehouse.length === 0) {
        setNewStock([]);
        setUsedStock([]);
        setFaultyStock([]);
        setSavedStock([]);
        setBundles([]);
        setShippedStock([]);
        return;
      }

      // Don't fetch data if ownerGroups are empty but warehouses are selected
      // This prevents API calls during auto-selection transitions
      if (
        selectedWarehouse.length > 0 &&
        (!selectedOwnerGroup || selectedOwnerGroup.length === 0)
      ) {
        console.log('🔄 Waiting for ownerGroup auto-selection to complete...');
        return;
      }

      isLoadingRef.current = true;
      setLoading(true);
      console.log('📊 Fetching inventory data for:', {
        warehouses: selectedWarehouse.length,
        ownerGroups: selectedOwnerGroup.length,
        ownerCodes: selectedOwnerCode.length,
      });

      const requestParams = {
        warehouses: selectedWarehouse,
        ownerCodes: selectedOwnerCode,
        ownerGroups: selectedOwnerGroup,
      };

      const [itemData, faultyData, savedData, bundleData, shippedData] = await Promise.all([
        get(URLS.items.getItemViewReportsNewAndUsedFaulty, {
          params: {
            search: { inStock: true, inBundle: false },
            ...requestParams,
          },
        }),
        get(URLS.items.getItemViewReportsFaulty, {
          params: {
            search: { inStock: false, inBundle: false, isSaved: false },
            ...requestParams,
          },
        }),
        get(URLS.items.listSavedStock, {
          params: requestParams,
        }),
        get(URLS.items.getBundledStock, {
          params: requestParams,
        }),
        get(URLS.items.getShippedItems, {
          params: requestParams,
        }),
      ]);

      setNewStock(itemData.data.result.find(d => d.itemCategory === 'new')?.data || []);
      setUsedStock(itemData.data.result.find(d => d.itemCategory === 'used')?.data || []);
      setFaultyStock(faultyData.data.result.find(d => d.itemCategory === 'faulty')?.data || []);
      console.log(faultyData.data.result, 'faultyData.data.result====');
      setSavedStock(savedData.data.result || []);
      setBundles(bundleData.data.result || []);
      console.log(shippedData.data.result, 'shippedData.data.result====');
      setShippedStock(shippedData.data.result || []);

      setLoading(false);
      isLoadingRef.current = false;
    } catch (error) {
      console.error('Error loading inventory data:', error);
      toast.error('Failed to load inventory data');
      setLoading(false);
      isLoadingRef.current = false;

      // Reset all data on error
      setNewStock([]);
      setUsedStock([]);
      setFaultyStock([]);
      setSavedStock([]);
      setBundles([]);
      setShippedStock([]);
    }
  }, [selectedWarehouse, selectedOwnerCode, selectedOwnerGroup]);

  // Debounced effect to wait for auto-selections to complete
  useEffect(() => {
    const timer = setTimeout(() => {
      getAllData();
    }, 300); // 300ms delay to allow auto-selection to complete

    return () => clearTimeout(timer);
  }, [selectedWarehouse, selectedOwnerGroup, selectedOwnerCode]);

  useEffect(() => {
    getSubFamily();
  }, [getSubFamily]);

  console.log('hiii');

  console.log(shippedStock, 'shippedStock====');

  const downloadAllItems = useCallback(async () => {
    try {
      // Block action if data is still loading
      if (loading || isLoadingRef.current) {
        return toast.info('Please wait for data to load before downloading!');
      }

      if (!selectedWarehouse.length) return toast.info('Please select a warehouse!');

      setDownloadingTotalStock(true);
      const { data } = await get(URLS.items.downloadTotalStock, {
        params: {
          warehouses: selectedWarehouse,
          ownerCodes: selectedOwnerCode,
          ownerGroups: selectedOwnerGroup,
        },
        responseType: 'blob',
      });
      downloadExcel(data, 'Total_Inventory');
      setDownloadingTotalStock(false);
    } catch (error) {
      setDownloadingTotalStock(false);
      console.error('Error downloading inventory:', error);
      toast.error('Failed to download inventory');
    }
  }, [selectedWarehouse, selectedOwnerCode, selectedOwnerGroup]);

  // Loading skeleton component
  const renderLoadingSkeleton = () => (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Fade in timeout={300}>
        <Box>
          {/* Header Skeleton */}
          <Card
            elevation={0}
            sx={{
              mb: 4,
              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              borderRadius: 4,
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.06) 100%)'
                : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Skeleton variant="rectangular" width={48} height={48} sx={{ borderRadius: 2 }} />
                  <Box>
                    <Skeleton variant="text" width={200} height={32} />
                    <Skeleton variant="text" width={300} height={20} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Skeleton
                    variant="rectangular"
                    width={180}
                    height={36}
                    sx={{ borderRadius: 2 }}
                  />
                  <Skeleton variant="circular" width={36} height={36} />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Grid Skeletons */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 3 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 3 }} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Container>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: isDarkMode ? 'rgba(0,0,0,0.02)' : 'rgba(0,0,0,0.01)',
        backgroundImage: isDarkMode
          ? 'radial-gradient(circle at 20% 50%, rgba(25,104,56,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(68,98,133,0.05) 0%, transparent 50%)'
          : 'radial-gradient(circle at 20% 50%, rgba(25,104,56,0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(68,98,133,0.03) 0%, transparent 50%)',
      }}
    >
      <DashBoardPageHeaderPage title="Inventory Reports" />

      {loading ? (
        renderLoadingSkeleton()
      ) : (
        <Box sx={{ py: { xs: 0.5, sm: 1 } }}>
          <Fade in timeout={500}>
            <Box>
              {/* Modern Header Section */}
              <Card
                elevation={0}
                sx={{
                  mb: { xs: 2, sm: 3 },
                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                  borderRadius: { xs: 2, sm: 2.5 },
                  background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: isDarkMode
                    ? '0 4px 16px rgba(0,0,0,0.2)'
                    : '0 4px 16px rgba(0,0,0,0.08)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: isDarkMode
                      ? '0 6px 24px rgba(0,0,0,0.3)'
                      : '0 6px 24px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 2, sm: 0 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                      <Box
                        sx={{
                          width: { xs: 36, sm: 40 },
                          height: { xs: 36, sm: 40 },
                          borderRadius: 1.5,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 2px 8px ${theme.palette.primary.main}30`,
                        }}
                      >
                        <Inventory2 sx={{ fontSize: { xs: 18, sm: 20 }, color: 'white' }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 0.25,
                          }}
                        >
                          Total Stock Overview
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, display: 'block' }}
                        >
                          Comprehensive inventory analysis across all categories
                        </Typography>
                      </Box>
                    </Box>

                    <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center">
                      <ShowComponent module={'Inventory'} action={'view'}>
                        <LoadingButton
                          onClick={handleDownloadReportOrderWise}
                          loading={downloading}
                          variant="contained"
                          size="small"
                          startIcon={<FileDownload />}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                            py: { xs: 0.5, sm: 0.75 },
                            px: { xs: 1, sm: 1.5 },
                            boxShadow: `0 2px 8px ${theme.palette.primary.main}30`,
                            '&:hover': {
                              boxShadow: `0 3px 12px ${theme.palette.primary.main}40`,
                            },
                          }}
                        >
                          {isMobile ? 'Excel' : 'Customer Excel'}
                        </LoadingButton>
                      </ShowComponent>

                      <IconButton
                        onClick={downloadAllItems}
                        disabled={downloadingTotalStock}
                        sx={{
                          width: { xs: 36, sm: 40 },
                          height: { xs: 36, sm: 40 },
                          borderRadius: 1.5,
                          bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                          border: `1px solid ${
                            isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
                          }`,
                          '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
                            transform: 'translateY(-2px)',
                            boxShadow: isDarkMode
                              ? '0 8px 24px rgba(0,0,0,0.3)'
                              : '0 8px 24px rgba(0,0,0,0.15)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                        title="Download All Items"
                      >
                        {downloadingTotalStock ? (
                          <CircularProgress size={20} sx={{ color: theme.palette.primary.main }} />
                        ) : (
                          <DownloadRounded
                            sx={{
                              fontSize: { xs: 16, sm: 18 },
                              color: theme.palette.primary.main,
                            }}
                          />
                        )}
                      </IconButton>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>

              {/* Enhanced Grid Layout */}
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12}>
                  <Fade in timeout={600}>
                    <Box>
                      <NewStock data={newStock} warehouseId={selectedWarehouse} />
                    </Box>
                  </Fade>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Fade in timeout={700}>
                    <Box>
                      <UsedStock data={usedStock} warehouseId={selectedWarehouse} />
                    </Box>
                  </Fade>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Fade in timeout={800}>
                    <Box>
                      <FaultyStock data={faultyStock} warehouseId={selectedWarehouse} />
                    </Box>
                  </Fade>
                </Grid>

                <Grid item xs={12}>
                  <Fade in timeout={900}>
                    <Box>
                      <SavedStock data={savedStock} warehouseId={selectedWarehouse} />
                    </Box>
                  </Fade>
                </Grid>

                <Grid item xs={12}>
                  <Fade in timeout={1000}>
                    <Box>
                      <BundledStock data={bundles} warehouseId={selectedWarehouse} />
                    </Box>
                  </Fade>
                </Grid>

                <Grid item xs={12}>
                  <Fade in timeout={1100}>
                    <Box>
                      <ShippedStock data={shippedStock} warehouseId={selectedWarehouse} />
                    </Box>
                  </Fade>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </Box>
      )}
    </Box>
  );
}
